import type { AssignmentCandidate, AssignmentNext, AssignmentSettings, SaveAssignmentSettingsBody } from "@gioia365/contracts";
import { previewNext, weightOf } from "@gioia365/domain/assignment";
import type { MessageKey } from "@gioia365/i18n";
import type { Translate } from "../../i18n/locale";

// La bozza di «Assegnazione dei Lead» (piano M6, Task 13, sul modello di `call-sequence-draft.ts`): le modifiche sono i soli campi
// toccati sopra la regola salvata, così una rilettura dopo un conflitto (`409 version_conflict`) rimette le modifiche sopra la regola
// nuova e i campi non toccati prendono quella. Funzioni pure: la bozza dalla regola salvata, il problema che impedisce di salvare
// (lo stesso controllo del server), «I prossimi 6 Lead nuovi» della bozza, ciò che cambia detto a parole e il corpo della richiesta.

/** I campi toccati, sopra la regola salvata. Un campo tornato uguale al salvato non è una modifica (`describeAssignmentChanges`). */
export type AssignmentEdits = Partial<AssignmentSettings>;

/** La regola che la pagina mostra: quella salvata con sopra le modifiche. */
export function draftOf(saved: AssignmentSettings, edits: AssignmentEdits): AssignmentSettings {
  const weights = edits.weights ?? saved.weights;
  return { ...saved, ...edits, people: [...(edits.people ?? saved.people)], ...(weights ? { weights: { ...weights } } : {}) };
}

/**
 * I pesi del turno da salvare: soltanto per le persone del turno e diversi da 1, come li scrive il server. Un peso dato a chi
 * poi esce dal turno non resta.
 */
export function weightsFor(people: readonly string[], weights: AssignmentSettings["weights"]): Record<string, number> | undefined {
  const kept = Object.fromEntries(people.flatMap((id) => (weights?.[id] !== undefined && weights[id] !== 1 ? [[id, weights[id]]] : [])));
  return Object.keys(kept).length > 0 ? kept : undefined;
}

/** I pesi dopo che una persona ha avuto il suo: 1 vuol dire nessun peso scritto. */
export function setWeight(weights: AssignmentSettings["weights"], membershipId: string, weight: number): Record<string, number> {
  const next = { ...(weights ?? {}) };
  if (weight === 1) delete next[membershipId];
  else next[membershipId] = weight;
  return next;
}

/**
 * Il corpo di `PUT /api/settings/assignment`: la regola intera e la versione su cui la bozza è stata pensata. Le scelte che la
 * pagina non mostra col modo scelto (le persone del turno con «Tutti a una persona», la persona unica con il turno) restano
 * quelle salvate: una casella toccata e poi lasciata non si salva di nascosto.
 */
export function saveBodyOf(saved: AssignmentSettings, draft: AssignmentSettings, expectedVersion: number): SaveAssignmentSettingsBody {
  const people = draft.mode === "round_robin" ? draft.people : saved.people;
  const weights = weightsFor(people, draft.mode === "round_robin" ? draft.weights : saved.weights);
  return {
    settings: {
      mode: draft.mode,
      people: [...people],
      single: draft.mode === "single" ? draft.single : saved.single,
      returning: draft.returning,
      ...(weights ? { weights } : {}),
    },
    expectedVersion,
  };
}

/**
 * Le persone del turno dopo che una casella è stata spuntata o tolta: sempre nell'ordine dell'elenco (alfabetico), così l'ordine
 * salvato non dipende dall'ordine dei clic. Le persone della regola che non sono nell'elenco non ci sono (il server non le manda).
 */
export function togglePerson(people: readonly string[], candidates: readonly AssignmentCandidate[], membershipId: string, on: boolean): string[] {
  const chosen = new Set(people);
  if (on) chosen.add(membershipId);
  else chosen.delete(membershipId);
  return candidates.filter((candidate) => chosen.has(candidate.membershipId)).map((candidate) => candidate.membershipId);
}

/** La persona unica quando si passa a «Tutti a una persona»: quella già scelta se è attiva, altrimenti la prima persona attiva. */
export function singleFor(draft: AssignmentSettings, candidates: readonly AssignmentCandidate[]): string | null {
  const active = candidates.filter((candidate) => candidate.active);
  if (draft.single !== null && active.some((candidate) => candidate.membershipId === draft.single)) return draft.single;
  return active[0]?.membershipId ?? null;
}

export type AssignmentProblem = "need_person" | "need_single";

/**
 * Ciò che impedisce di salvare, come lo controlla il server: il turno vuole almeno una persona attiva (mock-up) e «Tutti a una
 * persona» una persona attiva. «A nessuno» non ha niente da controllare.
 */
export function assignmentProblem(draft: AssignmentSettings, candidates: readonly AssignmentCandidate[]): AssignmentProblem | null {
  const active = new Set(candidates.filter((candidate) => candidate.active).map((candidate) => candidate.membershipId));
  if (draft.mode === "round_robin" && !draft.people.some((id) => active.has(id))) return "need_person";
  if (draft.mode === "single" && (draft.single === null || !active.has(draft.single))) return "need_single";
  return null;
}

/** Il problema a parole, per la barra della bozza. */
export function problemText(t: Translate, problem: AssignmentProblem): string {
  return problem === "need_person" ? t("assignmentSettings.problems.needPerson") : t("assignmentSettings.problems.needSingle");
}

/**
 * «I prossimi 6 Lead nuovi» per la bozza, dall'ultimo servito: lo stesso calcolo del server (`previewNext` del dominio), sulle
 * persone che oggi ricevono un Lead. Vuoto per «A nessuno» e quando nessuno è idoneo.
 */
export function draftNext(draft: AssignmentSettings, candidates: readonly AssignmentCandidate[], lastServed: string | null, servedInRow: number = 1): AssignmentNext[] {
  const eligible = new Set(candidates.filter((candidate) => candidate.active).map((candidate) => candidate.membershipId));
  const names = new Map(candidates.map((candidate) => [candidate.membershipId, candidate.displayName]));
  return previewNext(draft, lastServed, eligible, undefined, servedInRow).map((membershipId) => ({ membershipId, displayName: names.get(membershipId) ?? "" }));
}

const MODE_WORDS = {
  round_robin: "assignmentSettings.changes.modeRoundRobin",
  single: "assignmentSettings.changes.modeSingle",
  manual: "assignmentSettings.changes.modeManual",
} as const satisfies Record<AssignmentSettings["mode"], MessageKey>;

const sameIds = (left: readonly string[], right: readonly string[]) => left.length === right.length && left.every((id) => right.includes(id));

/** Ciò che cambia rispetto alla regola salvata, a parole e nell'ordine del mock-up: ogni frase è una modifica. */
export function describeAssignmentChanges(saved: AssignmentSettings, draft: AssignmentSettings, candidates: readonly AssignmentCandidate[], t: Translate): string[] {
  const names = new Map(candidates.map((candidate) => [candidate.membershipId, candidate.displayName]));
  const nameOf = (id: string) => names.get(id) ?? id;
  const sentences: string[] = [];
  if (saved.mode !== draft.mode) sentences.push(t("assignmentSettings.changes.mode", { after: t(MODE_WORDS[draft.mode]), before: t(MODE_WORDS[saved.mode]) }));
  if (draft.mode === "round_robin" && !sameIds(saved.people, draft.people)) {
    sentences.push(t("assignmentSettings.changes.people", { names: draft.people.map(nameOf).join(", ") }));
  }
  if (draft.mode === "round_robin" && draft.people.some((id) => weightOf(saved, id) !== weightOf(draft, id))) {
    sentences.push(t("assignmentSettings.changes.weights", { list: draft.people.map((id) => `${nameOf(id)} ${weightOf(draft, id)}`).join(", ") }));
  }
  if (draft.mode === "single" && saved.single !== draft.single && draft.single !== null) {
    sentences.push(t("assignmentSettings.changes.single", { name: nameOf(draft.single) }));
  }
  if (saved.returning !== draft.returning) {
    sentences.push(t(draft.returning === "keep_owner" ? "assignmentSettings.changes.returningKeep" : "assignmentSettings.changes.returningAsNew"));
  }
  return sentences;
}
